import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MousePointerClick } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrolleZu } from "@/lib/scroll";

const projects = [
  {
    n: "01",
    slug: "osteria-fortuna",
    name: "Osteria Fortuna",
    branche: "Restaurant",
    img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXN0YXVyYW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg4NzMxODI2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "02",
    slug: "halbritter-architekten",
    name: "Halbritter Architekten",
    branche: "Architektur",
    img: "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODg3MzE4MjZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "03",
    slug: "brenner-sohn",
    name: "Brenner & Sohn",
    branche: "Handwerk",
    img: "https://images.unsplash.com/photo-1659930087003-2d64e33181f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjcmFmdHNtYW5zaGlwJTIwd29vZHdvcmtpbmd8ZW58MHx8fHwxNzg4NzMxODI2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "04",
    slug: "maison-lumiere",
    name: "Maison Lumière",
    branche: "Beauty",
    img: "https://images.unsplash.com/photo-1603775020644-eb8decd79994?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwYmVhdXR5JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg4NzMxODI2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "05",
    slug: "kraftwerk-studio",
    name: "Kraftwerk Studio",
    branche: "Fitness",
    img: "https://images.pexels.com/photos/9545914/pexels-photo-9545914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    n: "06",
    slug: "aurum-automobile",
    name: "AURUM Automobile",
    branche: "Luxus",
    img: "https://images.pexels.com/photos/38570/lamborghini-car-speed-prestige-38570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

function ProjectCard({ p, onOpen }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !ref.current) return;
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onClick={(e) => onOpen(p, e.currentTarget)}
      data-testid={`project-card-${p.n}`}
      data-cursor
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onOpen(p, e.currentTarget)}
      className="group relative w-[80vw] shrink-0 cursor-pointer md:w-[38vw]"
    >
      <div className="relative h-[52vh] overflow-hidden rounded-xl border border-white/10 md:h-[58vh]">
        <img
          src={p.img}
          alt={`${p.name} — ${p.branche}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/90 via-transparent to-[#050505]/20" />
        <div
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(420px at var(--mx, 50%) var(--my, 50%), rgba(212,175,55,0.18), transparent 65%)",
          }}
        />
        <span className="text-stroke-gold absolute right-5 top-4 font-display text-6xl font-extrabold opacity-70">
          {p.n}
        </span>
        <span className="absolute left-5 top-5 flex translate-y-2 items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-black/60 px-4 py-1.5 text-[10px] tracking-[0.3em] text-[#FFE9A8] opacity-0 backdrop-blur-md transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <MousePointerClick size={11} /> WEBSITE BETRETEN
        </span>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="mb-1 text-[11px] tracking-[0.35em] text-[#D4AF37]">
            {p.branche.toUpperCase()} · STUDIE
          </p>
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">
              {p.name}
            </h3>
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#FFD700] opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-x-0 group-hover:opacity-100 md:-translate-x-2">
              <ArrowUpRight size={18} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [range, setRange] = useState(0);
  const [leaving, setLeaving] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        setRange(Math.max(0, trackRef.current.scrollWidth - window.innerWidth + 80));
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const { scrollYProgress } = useScroll({ target: sectionRef });
  const x = useTransform(scrollYProgress, [0, 1], [0, -range]);

  const open = (p, el) => {
    const img = el.querySelector("img");
    const r = (img || el).getBoundingClientRect();
    window.__lenis?.stop();
    setLeaving({ p, rect: { top: r.top, left: r.left, width: r.width, height: r.height } });
  };

  return (
    <section id="projekte" ref={sectionRef} data-testid="portfolio-section" className="relative z-10 h-[380vh]">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto mb-10 w-full max-w-7xl px-6 md:px-14">
          <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">
            GESTALTUNGSSTUDIEN · WEBDESIGN FÜR JEDE BRANCHE
          </p>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
              Eine digitale{" "}
              <em className="gold-text font-serif-accent normal-case italic">Ausstellung.</em>
            </h2>
            <p className="flex max-w-md items-start gap-2 text-xs leading-relaxed tracking-[0.2em] text-[#BDBDB6]">
              <MousePointerClick size={14} className="mt-0.5 shrink-0 text-[#D4AF37]" />
              RESTAURANT, ARCHITEKTUR, HANDWERK, BEAUTY, FITNESS, AUTOMOBIL — SECHS BRANCHEN,
              SECHS HANDSCHRIFTEN. FREI ERFUNDENE UNTERNEHMEN, ECHTE GESTALTUNG. JEDE WEBSITE
              LÄSST SICH BETRETEN.
            </p>
          </div>
        </div>
        <motion.div
          ref={trackRef}
          style={{ x }}
          className="flex w-max items-stretch gap-[6vw] pl-6 md:pl-14"
        >
          {projects.map((p) => (
            <ProjectCard key={p.n} p={p} onOpen={open} />
          ))}
          <div className="flex w-[60vw] shrink-0 items-center md:w-[30vw]">
            <div>
              <p className="gold-text font-serif-accent text-3xl italic md:text-4xl">Ihr Projekt?</p>
              <a
                href="#kontakt"
                onClick={(e) => {
                  e.preventDefault();
                  scrolleZu("#kontakt", { duration: 1.8 });
                }}
                data-testid="portfolio-cta-link"
                className="mt-4 inline-flex items-center gap-2 text-base tracking-[0.2em] text-[#FFD700] transition-colors hover:text-[#FFE9A8]"
              >
                JETZT ANFRAGEN <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {leaving && (
          <motion.div
            className="fixed inset-0 z-[70]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="project-transition-overlay"
          >
            <div className="absolute inset-0 bg-[#050505]/90" />
            <motion.img
              src={leaving.p.img}
              alt=""
              initial={{
                top: leaving.rect.top,
                left: leaving.rect.left,
                width: leaving.rect.width,
                height: leaving.rect.height,
                borderRadius: 12,
              }}
              animate={{
                top: 0,
                left: 0,
                width: window.innerWidth,
                height: window.innerHeight,
                borderRadius: 0,
              }}
              transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
              onAnimationComplete={() => navigate(`/projekt/${leaving.p.slug}`)}
              className="absolute object-cover"
            />
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="font-serif-accent text-4xl italic text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.8)] md:text-6xl">
                {leaving.p.name}
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
